import React, { Component } from 'react'
import 'react-vis/dist/style.css'
import { XYFrame, OrdinalFrame } from 'semiotic'
import * as moment from 'moment'
import { withStyles } from 'material-ui/styles'
import IconButton from 'material-ui/IconButton'
import ChevronLeftIcon from 'material-ui-icons/ChevronLeft'
import ChevronRightIcon from 'material-ui-icons/ChevronRight'
import { Button, Grid, Typography, Paper, LinearProgress } from 'material-ui'
import CheckIcon from 'material-ui-icons/Check'
import AddIcon from 'material-ui-icons/Add'
import { scaleTime } from 'd3-scale'

const gradient = (
  <linearGradient id="paleWoodGradient" x1="0%" y1="0%" x2="0%" y2="10%">
    <stop offset="0%" stopColor="rgb(80, 227, 194)" stopOpacity="0.8" />
    <stop offset="98%" stopColor="white" stopOpacity="1" />
  </linearGradient>
)

const gradientRecent = (
  <linearGradient id="paleWoodGradientRecent" x1="0%" y1="0%" x2="0%" y2="10%">
    <stop offset="0%" stopColor="rgb(80, 227, 194)" stopOpacity="0.8" />
    <stop offset="33%" stopColor="white" stopOpacity="1" />
  </linearGradient>
)

const axis = {
  orient: 'left',
  tickFormat: d => (
    <text
      key={`tick-${d}`}
      style={{ fill: '#cecece', textAnchor: 'end', fontSize: 10 }}
      y={-2}
    >
      {d}
    </text>
  ),
  tickValues: [95, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105],
  baseline: false,
  tickLineGenerator: ({ xy }) => (
    <path
      key={`tickLine-${xy.value}`}
      style={{ stroke: '#F0F0F0 ' }}
      d={`M${xy.x1},${xy.y1}L${xy.x2},${xy.y1}L${xy.x2},${xy.y1}L${xy.x1},${
        xy.y1
      }Z`}
    />
  )
}

const axesDailyOverall = [
  {
    orient: 'left',
    tickFormat: d => (
      <text
        key={`tick-${d}`}
        style={{ fill: '#cecece', textAnchor: 'end', fontSize: 10 }}
        y={-2}
      >
        {d}
      </text>
    ),
    baseline: false,
    tickValues: [95, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105],
    tickLineGenerator: ({ xy }) => (
      <path
        key={`tickLine-${xy.value}`}
        style={{ stroke: '#F0F0F0 ' }}
        d={`M${xy.x1},${xy.y1}L${xy.x2},${xy.y1}L${xy.x2},${xy.y1}L${xy.x1},${
          xy.y1
        }Z`}
      />
    )
  },
  {
    orient: 'bottom',
    ticks: 7,
    tickFormat: d => (
      <text
        key={`tick-${d}`}
        style={{ fill: '#cecece', textAnchor: 'end', fontSize: 10 }}
        y={-2}
      >
        {moment(d).format('M/D')}
      </text>
    ),
    baseline: false,
    tickLineGenerator: ({ xy }) => (
      <path
        key={`tickLine-date-${xy.value}`}
        style={{ stroke: '#F0F0F0 ' }}
        d={`M${xy.x1},${xy.y1}L${xy.x1},${xy.y2}L${xy.x1},${xy.y2}L${xy.x1},${
          xy.y1
        }Z`}
      />
    )
  }
]

const axesDailyOverallRecent = [
  {
    orient: 'left',
    tickFormat: d => (
      <text
        key={`tick-${d}`}
        style={{ fill: '#cecece', textAnchor: 'end', fontSize: 10 }}
        y={-2}
      >
        {d}
      </text>
    ),
    baseline: false,
    tickValues: [95, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105],
    tickLineGenerator: ({ xy }) => (
      <path
        key={`tickLine-${xy.value}`}
        style={{ stroke: '#F0F0F0 ' }}
        d={`M${xy.x1},${xy.y1}L${xy.x2},${xy.y1}L${xy.x2},${xy.y1}L${xy.x1},${
          xy.y1
        }Z`}
      />
    )
  },
  {
    orient: 'bottom',
    ticks: 7,
    tickFormat: d => (
      <text
        key={`tick-${d}`}
        style={{ fill: '#cecece', textAnchor: 'end', fontSize: 10 }}
        y={-2}
      >
        {moment(d).format('M/D')}
      </text>
    ),
    baseline: false,
    tickLineGenerator: ({ xy }) => (
      <path
        key={`tickLine-date-${xy.value}`}
        style={{ stroke: 'white' }}
        d={`M${xy.x1},${xy.y1}L${xy.x1},${xy.y2}L${xy.x1},${xy.y2}L${xy.x1},${
          xy.y1
        }Z`}
      />
    )
  }
]

const styles = theme => ({
  tooltipContent: {
    background: 'white',
    border: 1,
    color: 'black',
    padding: 10
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  textField: {
    marginLeft: theme.spacing.unit,
    marginRight: theme.spacing.unit,
    width: 200
  },
  paperRoot: {
    margin: theme.spacing.unit,
    boxShadow: '0px 0px 10px #eeeeee',
    alignItems: 'center',
    padding: 10,
    height: 82
  },
  paperRootGrid: {
    padding: theme.spacing.unit
  },
  linearColorPrimary: {
    backgroundColor: 'rgba(0, 0, 0, .05)'
  },
  linearBarColorPrimary: {
    backgroundColor: 'rgba(80, 227, 194, .4)'
  },
  linearBarColorPrimaryComplete: {
    backgroundColor: 'lightgreen'
  },
  linearBarColorPrimaryWhite: {
    backgroundColor: 'white'
  }
})

const hoursBar = ({ data, rScale, adjustedSize, margin }) => {
  const renderedPieces = []
  const keys = Object.keys(data)

  keys.forEach(key => {
    //Only one piece of data per column though we'll render multiple graphical elements
    const column = data[key]

    //You can return an array of graphics or an array of objects with extra data (see the Waterfall chart demo)
    const markObject = (
      <g key={`piece-${key}`}>
        {column.pieceData.map(
          (piece, index) =>
            piece.data.weightRecord > 0 && (
              <g key={`g-${index}-${key}`}>
                <rect
                  key={`rect-${index}-${key}`}
                  fill={
                    piece.data.log === 'grey'
                      ? 'white'
                      : piece.data.weightRecord !== 0 &&
                        piece.data.weightRecord === piece.data.minDailyWeight
                        ? 'rgba(39, 199, 167, .8)'
                        : 'lightgrey'
                  }
                  stroke={''}
                  opacity={0.5}
                  width={column.width * 1.1}
                  height={column.width * 0.7}
                  x={column.x * 0.98}
                  y={piece.scaledVerticalValue}
                  rx={3}
                  ry={3}
                />
                <text
                  key={`text-LB-${key}`}
                  x={column.x + column.width / 2.8 - adjustedSize[0] / 32}
                  y={piece.scaledVerticalValue + adjustedSize[0] / 21}
                  fontSize={adjustedSize[0] / 28}
                  fontWeight={
                    piece.data.weightRecord === piece.data.minDailyWeight &&
                    moment(new Date(piece.data.date)).format('l') ===
                      moment().format('l')
                      ? 800
                      : 300
                  }
                  fill={'white'}
                >
                  {piece.data.weightRecord}
                </text>
                {piece.data.weightRecord < 98 ? (
                  <text
                    key={`text-${key}`}
                    x={column.x + column.width / 0.8 - adjustedSize[0] / 32}
                    y={piece.scaledVerticalValue + adjustedSize[0] / 70}
                    fontSize={adjustedSize[0] / 26}
                    fill={'lightblue'}
                  >
                    ★
                  </text>
                ) : (
                  <text key={`text-${key}`} />
                )}
              </g>
            )
        )}
        }
      </g>
    )

    renderedPieces.push(markObject)
  })
  return renderedPieces
}

const goalBar = ({ data, rScale, adjustedSize, margin }) => {
  const renderedPieces = []
  const keys = Object.keys(data)

  keys.forEach(key => {
    //Only one piece of data per column though we'll render multiple graphical elements
    const column = data[key]

    //You can return an array of graphics or an array of objects with extra data (see the Waterfall chart demo)
    const markObject = (
      <g key={`piece-${key}`}>
        {column.pieceData.map(
          (piece, index) =>
            piece.data.weightRecord > 0 && (
              <g key={`g-${index}-${key}}`}>
                {piece.data.type === 'weeklyGoal' && (
                  <rect
                    key={`rect-${index}-${key}`}
                    fill={piece.data.indicatorColor}
                    stroke={''}
                    opacity={0.5}
                    width={column.width * 3.2 > 0 ? column.width * 3.2 : 16}
                    height={piece.scaledValue}
                    x={column.x * 0.98}
                    y={piece.scaledVerticalValue}
                    rx={2}
                    ry={3}
                  />
                )}
                {piece.data.type === 'weeklyMinAvg' && (
                  <rect
                    key={`rect-${index}-${key}`}
                    fill={
                      piece.data.indicatorColor !== 'lightblue'
                        ? piece.data.indicatorColor
                        : 'rgba(0, 0, 0, 0)'
                    }
                    stroke={
                      piece.data.indicatorColor === 'lightblue'
                        ? 'lightblue'
                        : ''
                    }
                    width={column.width * 3.2 > 0 ? column.width * 3.2 : 16}
                    height={column.width * 2.2 > 0 ? column.width * 2.2 : 12}
                    x={column.x * 0.98}
                    y={piece.scaledVerticalValue}
                    rx={1}
                    ry={1}
                  />
                )}
                {piece.data.indicatorColor !== 'lightblue' && (
                  <text
                    key={`text-LB-${key}`}
                    x={column.x * 0.98}
                    y={
                      piece.data.type === 'weeklyMinAvg'
                        ? piece.scaledVerticalValue + column.width * 1.8
                        : piece.scaledVerticalValue - column.width * 0.2
                    }
                    fontSize={7}
                    fontWeight={piece.data.type === 'weeklyMinAvg' ? 800 : 300}
                    fill={
                      piece.data.type === 'weeklyMinAvg' ? 'white' : 'white'
                    }
                  >
                    {piece.data.weightRecord}
                  </text>
                )}
              </g>
            )
        )}
        }
      </g>
    )

    renderedPieces.push(markObject)
  })
  return renderedPieces
}

const recent5Bar = ({ data, rScale, adjustedSize, margin }) => {
  const renderedPieces = []
  const keys = Object.keys(data)

  keys.forEach(key => {
    //Only one piece of data per column though we'll render multiple graphical elements
    const column = data[key]

    //You can return an array of graphics or an array of objects with extra data (see the Waterfall chart demo)
    const markObject = (
      <g key={`piece-${key}`}>
        {column.pieceData.map(
          (piece, index) =>
            piece.data.weightRecord > 0 && (
              <g key={`g-${index}-${key}}`}>
                {piece.data.type === 'weeklyGoal' && (
                  <rect
                    key={`rect-${index}-${key}`}
                    fill={piece.data.indicatorColor}
                    stroke={''}
                    opacity={0.5}
                    width={column.width > 0 ? column.width : 73}
                    height={piece.scaledValue}
                    x={column.x * 0.98 - 73}
                    y={piece.scaledVerticalValue}
                    rx={2}
                    ry={3}
                  />
                )}
                {piece.data.type === 'weeklyMinAvg' && (
                  <rect
                    key={`rect-${index}-${key}`}
                    fill={
                      piece.data.indicatorColor !== 'lightblue'
                        ? piece.data.indicatorColor
                        : 'rgba(0, 0, 0, 0)'
                    }
                    stroke={
                      piece.data.indicatorColor === 'lightblue'
                        ? 'lightblue'
                        : ''
                    }
                    width={column.width > 0 ? column.width : 73}
                    height={column.width / 4 > 0 ? column.width / 4 : 18}
                    x={column.x * 0.98 - 73}
                    y={piece.scaledVerticalValue}
                    rx={1}
                    ry={1}
                  />
                )}
                {
                  <text
                    key={`text-LB-${key}`}
                    x={
                      column.width > 0
                        ? column.x + column.width * 0.2
                        : column.x + 15 - 78
                    }
                    y={
                      piece.data.type === 'weeklyMinAvg'
                        ? column.width > 0
                          ? piece.scaledVerticalValue + column.width * 0.2
                          : piece.scaledVerticalValue + 15
                        : piece.scaledVerticalValue - column.width * 0.1
                    }
                    fontSize={16}
                    fontWeight={piece.data.type === 'weeklyMinAvg' ? 300 : 100}
                    fill={
                      piece.data.type === 'weeklyMinAvg'
                        ? piece.data.indicatorColor === 'lightblue'
                          ? 'lightblue'
                          : 'green'
                        : 'lightgrey'
                    }
                  >
                    {piece.data.type !== 'dailyMinWeight' &&
                      piece.data.weightRecord}
                  </text>
                }
                {piece.data.type === 'dailyMinWeight' && (
                  <rect
                    key={`rect-daily-${index}-${key}`}
                    fill={piece.data.indicatorColor}
                    stroke={''}
                    opacity={1}
                    width={column.width > 0 ? column.width : 6}
                    height={column.width / 4 > 0 ? column.width / 4 : 6}
                    x={column.x - 8}
                    y={piece.scaledVerticalValue}
                    rx={3}
                    ry={3}
                  />
                )}
              </g>
            )
        )}
        }
      </g>
    )

    renderedPieces.push(markObject)
  })
  return renderedPieces
}

const recentBar = ({ data, rScale, adjustedSize, margin }) => {
  const renderedPieces = []
  const keys = Object.keys(data)
  //todo: 73, 60, 52, 30
  const widthBar = 30

  keys.forEach(key => {
    //Only one piece of data per column though we'll render multiple graphical elements
    const column = data[key]

    //You can return an array of graphics or an array of objects with extra data (see the Waterfall chart demo)
    const markObject = (
      <g key={`piece-${key}`}>
        {column.pieceData.map(
          (piece, index) =>
            piece.data.weightRecord > 0 && (
              <g key={`g-${index}-${key}}`}>
                {piece.data.type === 'weeklyGoal' && (
                  <rect
                    key={`rect-${index}-${key}`}
                    fill={piece.data.indicatorColor}
                    stroke={''}
                    opacity={0.5}
                    width={column.width > 0 ? column.width : widthBar}
                    height={piece.scaledValue}
                    x={column.x * 0.98 - widthBar}
                    y={piece.scaledVerticalValue}
                    rx={2}
                    ry={3}
                  />
                )}
                {piece.data.type === 'weeklyMinAvg' && (
                  <rect
                    key={`rect-${index}-${key}`}
                    fill={
                      piece.data.indicatorColor !== 'lightblue'
                        ? piece.data.indicatorColor
                        : 'rgba(0, 0, 0, 0)'
                    }
                    stroke={
                      piece.data.indicatorColor === 'lightblue'
                        ? 'lightblue'
                        : ''
                    }
                    width={column.width > 0 ? column.width : widthBar}
                    height={column.width / 4 > 0 ? column.width / 4 : 18}
                    x={column.x * 0.98 - widthBar}
                    y={piece.scaledVerticalValue}
                    rx={1}
                    ry={1}
                  />
                )}
                {
                  <text
                    key={`text-LB-${key}`}
                    x={
                      column.width > 0
                        ? column.x + column.width * 0.2
                        : column.x - widthBar + 1
                    }
                    y={
                      piece.data.type === 'weeklyMinAvg'
                        ? column.width > 0
                          ? piece.scaledVerticalValue + column.width * 0.2
                          : piece.scaledVerticalValue + 15
                        : piece.scaledVerticalValue - column.width * 0.1
                    }
                    fontSize={12}
                    fontWeight={piece.data.type === 'weeklyMinAvg' ? 300 : 100}
                    fill={
                      piece.data.type === 'weeklyMinAvg'
                        ? piece.data.indicatorColor === 'lightblue'
                          ? 'lightblue'
                          : 'green'
                        : 'lightgrey'
                    }
                  >
                    {piece.data.type !== 'dailyMinWeight' &&
                      piece.data.weightRecord}
                  </text>
                }
                {piece.data.type === 'dailyMinWeight' && (
                  <rect
                    key={`rect-daily-${index}-${key}`}
                    fill={piece.data.indicatorColor}
                    stroke={''}
                    opacity={1}
                    width={column.width > 0 ? column.width : 4}
                    height={column.width / 4 > 0 ? column.width / 4 : 4}
                    x={column.x - 8}
                    y={piece.scaledVerticalValue}
                    rx={2}
                    ry={2}
                  />
                )}
              </g>
            )
        )}
        }
      </g>
    )

    renderedPieces.push(markObject)
  })
  return renderedPieces
}

@withStyles(styles)
export default class AlchemyBodyWeightWeeklyView extends Component {
  constructor(props) {
    super(props)
    this.state = {
      dateSeleted: new Date(moment())
        .toISOString('yyyy-MM-dd')
        .substring(0, 10),
      weekNo: moment()
        .endOf('week')
        .format('l')
    }
    this.nextWeek = this.nextWeek.bind(this)
    this.prevWeek = this.prevWeek.bind(this)
    this.setToday = this.setToday.bind(this)
  }

  setToday = () => {
    this.setState({
      weekNo: moment()
        .endOf('week')
        .format('l')
    })
  }

  prevWeek = () => {
    const prevWeekNo = moment(new Date(this.state.weekNo))
      .subtract(7, 'days')
      .endOf('week')
      .format('l')
    this.setState({ weekNo: prevWeekNo })
  }

  nextWeek = () => {
    const nextWeekNo = moment(new Date(this.state.weekNo))
      .add(7, 'days')
      .endOf('week')
      .format('l')
    this.setState({ weekNo: nextWeekNo })
  }

  addBlankDate = data => {
    var iDateStart = moment(new Date(this.state.weekNo)).startOf('week')
    var iDateEnd = moment(new Date(this.state.weekNo)).endOf('week')
    var daysWeek = iDateEnd.diff(iDateStart, 'days') + 1

    for (var i = 0; i < daysWeek; i++) {
      var blankDate = iDateStart
      var isSame = false

      for (var j in data) {
        if (
          moment(new Date(data[j].date)).format('l') === blankDate.format('l')
        ) {
          isSame = true
        }
      }
      if (!isSame) {
        data.splice(i, 0, {
          date: blankDate.format('l'),
          week: blankDate.week(),
          minDailyWeight: 0,
          weightRecord: 0,
          log: 'blank'
        })
        isSame = false
      }

      iDateStart = moment(blankDate).add(1, 'd')
    }

    var weekCalStart = moment(new Date(this.state.weekNo))
      .startOf('week')
      .week()
    for (i in data) {
      data[i].week = data[i].week - weekCalStart
      if (data[i].week < 0) {
        data[i].week += 52
      }
      data[i]['weekday'] = moment(new Date(data[i].date)).weekday()
    }
    var firstDate = moment(new Date(this.state.weekNo)).startOf('week')
    var addPrevDays = firstDate.weekday()
    var blankDatePrev = firstDate
    for (i = 0; i < addPrevDays; i++) {
      blankDatePrev = blankDatePrev.subtract(1, 'd')

      data.splice(0, 0, {
        date: blankDatePrev.format('l'),
        week: blankDatePrev.week() - weekCalStart,
        minDailyWeight: 0,
        weightRecord: 0,
        log: 'grey',
        weekday: blankDatePrev.weekday()
      })
    }

    return data
  }

  //get weekly avg based on daily min weight
  getWeightAvg = date => {
    var wieghts = []
    var avg = ''
    const arrAvg = arr => arr.reduce((a, b) => a + b, 0) / arr.length
    this.props.data.forEach((w, i) => {
      if (
        moment(new Date(w[0]))
          .endOf('week')
          .format('l') === date
      ) {
        wieghts.push(w)
      }
    })

    if (wieghts.length === 0) {
      avg = ''
    } else if (wieghts.length === 1) {
      avg = wieghts[0][2]
    } else if (wieghts.length > 1) {
      var dailyMinWeights = []
      wieghts.forEach((w, i) => {
        dailyMinWeights.push(parseFloat(w[2], 10))
      })
      avg = arrAvg(dailyMinWeights).toFixed(1)
    }

    return avg
  }

  updatePred = (date, weight, goal) => {
    var weightPred = ''
    var weeks = moment(new Date(date)).diff(moment(), 'weeks')
    if (moment(new Date(date)) <= moment().endOf('week')) {
      weightPred = ''
    } else {
      weightPred = weight - (weeks + 0) * 0.5
      if (weightPred <= goal) {
        weightPred = ''
      }
    }
    return weightPred
  }

  getDailyMinWeight = (dailyData, date) => {
    var dailyMinWeight = ''
    let weights = dailyData.filter(
      w => moment(new Date(w.date)).format('l') === date
    )
    if (weights.length === 0) {
      dailyMinWeight = ''
    } else {
      dailyMinWeight = weights[0].minDailyWeight
    }
    return dailyMinWeight ? parseFloat(dailyMinWeight, 10) : dailyMinWeight
  }

  render() {
    if (this.props.data && this.props.data.loading) {
      return <div>Loading</div>
    }

    if (this.props.data && this.props.data.error) {
      return <div>Error</div>
    }

    const { classes } = this.props

    const dataCCHours = this.props.data

    let dataCal = []
    const keys = Object.keys(dataCCHours)
    var x = 0
    var records = []
    var recordsMin = []
    keys.forEach((key, index) => {
      const eachDay = dataCCHours[key]
      const weekNo = moment(new Date(eachDay[0])).week()

      eachDay[1].forEach((w, i) => {
        dataCal.splice(x, 0, {
          date: eachDay[0],
          week: weekNo,
          minDailyWeight: eachDay[2].toFixed(1),
          weightRecord: parseFloat(w.weightLB, 10).toFixed(1),
          log: ''
        })
        x += 1
        if (
          moment(new Date(eachDay[0]))
            .endOf('week')
            .format('l') === this.state.weekNo
        ) {
          records.push(parseFloat(w.weightLB, 10))
          recordsMin.push(parseFloat(eachDay[2], 10))
        }
      })
    })

    var weightMax = Math.max.apply(null, records) + 0.5
    var weightMin = Math.min.apply(null, records) - 0.5
    const arrAvg = arr => arr.reduce((a, b) => a + b, 0) / arr.length
    var weightAvg = arrAvg(recordsMin).toFixed(1)
    var weightAvgNum = arrAvg(recordsMin)

    dataCal = this.addBlankDate(dataCal)
    let dataCalWeek = dataCal.filter(
      w =>
        moment(new Date(w.date))
          .endOf('week')
          .format('l') === this.state.weekNo
    )

    //goal burn down chart data
    var goalStartDate = moment(new Date('1/6/2019'))
      .endOf('week')
      .format('l')
    var weightStart = this.getWeightAvg(goalStartDate)
    var weightEngGoal = 95
    var goalInterval = 0.5
    var burndown = []

    //add prediction
    let moments = this.props.data.map(d => moment(new Date(d[0]))),
      maxDate = moment.max(moments)
    let lastWeight = this.getWeightAvg(maxDate.endOf('week').format('l'))

    for (var i = 0; i <= (weightStart - weightEngGoal) / goalInterval; i++) {
      var weeklyAvgBurndown = ''
      var dateGoal = moment(new Date(goalStartDate))
        .add(i, 'weeks')
        .format('l')
      weeklyAvgBurndown = this.getWeightAvg(dateGoal)

      burndown.splice(i, 0, {
        id: i,
        date: dateGoal,
        type: 'weeklyGoal',
        weightRecord: weightStart - i * 0.5,
        indicatorColor: 'lightgrey'
      })
      burndown.splice(i, 0, {
        id: i,
        date: dateGoal,
        type: 'weeklyMinAvg',
        weightRecord: weeklyAvgBurndown
          ? parseFloat(weeklyAvgBurndown, 10)
          : this.updatePred(dateGoal, lastWeight, weightEngGoal),
        indicatorColor: weeklyAvgBurndown
          ? parseFloat(weeklyAvgBurndown, 10) > weightStart - i * 0.5
            ? 'pink'
            : 'lightgreen'
          : 'lightblue'
      })
    }

    var burndownRecords = []
    burndown.forEach((r, i) => {
      r.weightRecord !== '' && burndownRecords.push(r.weightRecord)
    })

    var weightBurndownMax = Math.max.apply(null, burndownRecords) + 0.5
    var weightBurndownMin = Math.min.apply(null, burndownRecords) - 0.5

    var todayWeights = dataCal.filter(
      w => moment(new Date(w.date)).format('l') === moment().format('l')
    )

    var todayWeight = ''

    if (todayWeights.length === 0) {
      todayWeight = ''
    } else {
      todayWeight = todayWeights[0].minDailyWeight
    }

    //weekly goal vs avg
    var weightGoal = ''
    let weekGoals = burndown.filter(
      w =>
        moment(new Date(w.date))
          .endOf('week')
          .format('l') === this.state.weekNo && w.type === 'weeklyGoal'
    )
    if (weekGoals.length === 0) {
      weightGoal = ''
    } else if (weekGoals.length === 1) {
      weightGoal = parseFloat(weekGoals[0].weightRecord, 10)
    } else {
      console.log('no goal yet')
    }
    var diff = weightAvgNum - weightGoal
    var percentGoal =
      diff <= 0 ? 100 : diff > 0.5 ? 0 : parseInt((1 - diff / 0.5) * 100, 10)

    //chart on recent weeks
    const burndownRecent = burndown.filter(
      w =>
        moment(new Date(w.date)) <=
        moment()
          .startOf('week')
          .add(13, 'days')
    )
    let momentsRecent = burndownRecent.map(d => moment(new Date(d.date)))
    let maxDateRecent = moment.max(momentsRecent)
    let minDateRecent = moment.min(momentsRecent)
    let minDataRecentFullWeek = minDateRecent.subtract(7, 'days')
    let days = maxDateRecent.diff(minDataRecentFullWeek, 'days')
    for (i = 0; i < days; i++) {
      var dateI = minDataRecentFullWeek
      dateI = dateI.add(1, 'day')
      burndownRecent.splice(0, 0, {
        id: i,
        date: dateI.format('l'),
        type: 'dailyMinWeight',
        weightRecord: this.getDailyMinWeight(dataCal, dateI.format('l')),
        indicatorColor: 'grey'
      })
    }

    const burndownRecent5 = burndownRecent.filter(
      w =>
        moment(new Date(w.date)) >=
        moment()
          .subtract(21, 'days')
          .startOf('week')
    )

    var burndownRecordsRecent5 = []
    burndownRecent5.forEach((r, i) => {
      r.weightRecord > 1 && burndownRecordsRecent5.push(r.weightRecord)
    })

    var weightBurndownMaxRecent5 =
      Math.max.apply(null, burndownRecordsRecent5) + 0.5
    var weightBurndownMinRecent5 =
      Math.min.apply(null, burndownRecordsRecent5) - 0.5

    var burndownRecordsRecent = []
    burndownRecent.forEach((r, i) => {
      r.weightRecord > 1 && burndownRecordsRecent.push(r.weightRecord)
    })

    var weightBurndownMaxRecent =
      Math.max.apply(null, burndownRecordsRecent) + 0.7
    var weightBurndownMinRecent =
      Math.min.apply(null, burndownRecordsRecent) - 0.7

    return (
      <div
        style={{
          width: this.props.width,
          display: 'inline-block'
        }}
      >
        <div
          style={{
            width: '100%'
          }}
        >
          <Grid
            container
            direction="row"
            justify="center"
            alignItems="baseline"
            spacing={0}
            className={classes.paperRootGrid}
          >
            <Grid item xs={6}>
              <Paper className={classes.paperRoot}>
                <Typography variant="caption" style={{ color: 'lightgrey' }}>
                  Today
                </Typography>
                {todayWeight > 0 ? (
                  <Typography
                    variant="display1"
                    style={{ color: 'lightblue', padding: 5 }}
                  >
                    {todayWeight}
                  </Typography>
                ) : (
                  <IconButton className="btn btn-default btn-style">
                    <AddIcon />
                  </IconButton>
                )}
                <Typography style={{ color: 'lightgrey', fontSize: 12 }}>
                  {moment().format('l')}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={100}
                  classes={{
                    colorPrimary: classes.linearColorPrimary,
                    barColorPrimary: classes.linearBarColorPrimaryWhite
                  }}
                />
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper className={classes.paperRoot}>
                <Typography variant="caption" style={{ color: 'lightgrey' }}>
                  Weekly Average (lb) ⓘ
                </Typography>
                <Typography
                  variant="display1"
                  style={{ color: 'rgba(168, 169, 255,0.5)', padding: 5 }}
                >
                  {weightAvg}
                </Typography>

                <Typography style={{ color: 'lightgrey', fontSize: 12 }}>
                  {diff > 0 ? diff.toFixed(1) + ' lb to ' : ''}
                  Goal {weightGoal} lb
                  {percentGoal >= 100 ? (
                    <CheckIcon style={{ color: 'green', fontSize: 12 }} />
                  ) : (
                    ''
                  )}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={percentGoal}
                  classes={{
                    colorPrimary: classes.linearColorPrimary,
                    barColorPrimary:
                      percentGoal >= 100
                        ? classes.linearBarColorPrimaryComplete
                        : classes.linearBarColorPrimary
                  }}
                />
              </Paper>
            </Grid>
          </Grid>
        </div>
        <div
          id="weeks"
          style={{
            textAlign: 'center',
            width: '70%',
            marginLeft: window.innerWidth * 0.1,
            marginRight: window.innerWidth * 0.1
          }}
        >
          <Grid
            container
            direction="row"
            justify="center"
            alignItems="baseline"
            spacing={0}
          >
            <Grid item xs={2} />
            <Grid item xs={2}>
              <IconButton
                onClick={() => this.prevWeek()}
                disabled={this.state.isPaused}
                className="btn btn-default btn-style"
              >
                <ChevronLeftIcon style={{ fontSize: 30 }} />
              </IconButton>
            </Grid>
            <Grid item xs={4}>
              <span style={{ fontSize: 14 }}>
                {moment(new Date(this.state.weekNo))
                  .startOf('week')
                  .format('M/D')}{' '}
                -{' '}
                {moment(new Date(this.state.weekNo))
                  .endOf('week')
                  .format('M/D')}
              </span>
            </Grid>
            <Grid item xs={2}>
              <IconButton
                onClick={() => this.nextWeek()}
                disabled={this.state.isPaused}
                className="btn btn-default btn-style"
              >
                <ChevronRightIcon style={{ fontSize: 30 }} />
              </IconButton>
            </Grid>
            <Grid item xs={2}>
              {moment(new Date(this.state.weekNo))
                .endOf('week')
                .format('l') ===
              moment()
                .endOf('week')
                .format('l') ? (
                ''
              ) : (
                <Button
                  onClick={() => this.setToday()}
                  className={classes.icon}
                  size="small"
                >
                  <span style={{ fontSize: 10 }}>Today</span>
                </Button>
              )}
            </Grid>
          </Grid>
        </div>
        {dataCalWeek &&
          dataCalWeek.length !== 0 && (
            <div
              style={{
                alignItems: 'center',
                position: 'relative',
                height: this.props.height,
                width: this.props.width
              }}
            >
              <div style={{ position: 'absolute' }}>
                <OrdinalFrame
                  size={[this.props.width, this.props.height * 0.8]}
                  data={dataCalWeek}
                  type={{
                    type: hoursBar,
                    rotate: 180
                  }}
                  axis={axis}
                  rAccessor={d => d.weightRecord}
                  oAccessor={d => moment(new Date(d.date)).format('d')}
                  sortO={(a, b) => a > b}
                  rExtent={[weightMin, weightMax]}
                  projection="vertical"
                  oPadding={this.props.width / 28}
                  margin={{
                    left: this.props.width / 14,
                    right: this.props.width / 40,
                    top: this.props.height / 24,
                    bottom: this.props.height / 9.6
                  }}
                  oLabel={d => (
                    <text
                      key={`label-${d}`}
                      y={15}
                      fill={'#cecece'}
                      textAnchor="middle"
                      fontSize={14}
                    >
                      {d === '0'
                        ? 'S'
                        : d === '1'
                          ? 'M'
                          : d === '2'
                            ? 'T'
                            : d === '3'
                              ? 'W'
                              : d === '4' ? 'T' : d === '5' ? 'F' : 'S'}
                    </text>
                  )}
                  annotationSettings={{
                    layout: {
                      type: 'marginalia',
                      orient: 'right',
                      textPadding: this.props.width / 21
                    }
                  }}
                />
              </div>
            </div>
          )}

        {burndownRecent &&
          burndownRecent.length !== 0 && (
            <div
              style={{
                alignItems: 'center',
                position: 'relative',
                height: this.props.height,
                width: this.props.width
              }}
            >
              <div style={{ position: 'absolute' }}>
                <XYFrame
                  size={[this.props.width, this.props.height]}
                  margin={{
                    left: this.props.width / 14,
                    right: this.props.width / 40,
                    top: this.props.height / 24,
                    bottom: this.props.height / 9.6
                  }}
                  xScaleType={scaleTime()}
                  xAccessor={d => new Date(d.date)}
                  yAccessor={d => d.type === 'dailyMinWeight' && d.weightRecord}
                  areas={[{ label: 'LB', coordinates: burndownRecent }]}
                  areaStyle={() => ({ fill: 'url(#paleWoodGradient)' })}
                  additionalDefs={gradient}
                  defined={d => d.weightRecord > 1}
                  axes={axesDailyOverall}
                  yExtent={[weightBurndownMinRecent, weightBurndownMaxRecent]}
                />
              </div>
            </div>
          )}

        {burndown &&
          burndown.length !== 0 && (
            <div
              style={{
                alignItems: 'center',
                position: 'relative',
                height: this.props.height,
                width: this.props.width
              }}
            >
              <div style={{ position: 'absolute' }}>
                <OrdinalFrame
                  size={[this.props.width, this.props.height]}
                  data={burndown}
                  type={{
                    type: goalBar,
                    rotate: 180
                  }}
                  axis={axis}
                  rAccessor={d => d.weightRecord}
                  oAccessor={d => moment(new Date(d.date)).format('YYYY/MM/DD')}
                  sortO={(a, b) => a > b}
                  rExtent={[weightBurndownMin, weightBurndownMax]}
                  projection="vertical"
                  oPadding={this.props.width / 28}
                  margin={{
                    left: this.props.width / 14,
                    right: this.props.width / 40,
                    top: this.props.height / 24,
                    bottom: this.props.height / 9.6
                  }}
                  oLabel={d => (
                    <text
                      key={`label-${d}`}
                      y={2}
                      fill={'#cecece'}
                      textAnchor="middle"
                      fontSize={7}
                    >
                      {moment(new Date(d)).format('M/D')}
                    </text>
                  )}
                  annotationSettings={{
                    layout: {
                      type: 'marginalia',
                      orient: 'right',
                      textPadding: this.props.width / 21
                    }
                  }}
                />
              </div>
            </div>
          )}

        {burndownRecent5 &&
          burndownRecent5.length !== 0 && (
            <div
              style={{
                alignItems: 'center',
                position: 'relative',
                height: this.props.height,
                width: this.props.width
              }}
            >
              <div style={{ position: 'absolute' }}>
                <XYFrame
                  size={[this.props.width, this.props.height]}
                  margin={{
                    left: this.props.width / 14,
                    right: this.props.width / 40,
                    top: this.props.height / 24,
                    bottom: this.props.height / 9.6
                  }}
                  xScaleType={scaleTime()}
                  xAccessor={d => new Date(d.date)}
                  yAccessor={d => d.type === 'dailyMinWeight' && d.weightRecord}
                  areas={[{ label: 'LB', coordinates: burndownRecent5 }]}
                  areaStyle={() => ({ fill: 'url(#paleWoodGradientRecent)' })}
                  additionalDefs={gradientRecent}
                  defined={d => d.weightRecord > 1}
                  axes={axesDailyOverallRecent}
                  yExtent={[weightBurndownMinRecent5, weightBurndownMaxRecent5]}
                />
              </div>
              <div style={{ position: 'absolute' }}>
                <OrdinalFrame
                  size={[this.props.width, this.props.height]}
                  data={burndownRecent5}
                  type={{
                    type: recent5Bar,
                    rotate: 180
                  }}
                  axis={axis}
                  rAccessor={d => d.weightRecord}
                  oAccessor={d => moment(new Date(d.date)).format('YYYY/MM/DD')}
                  sortO={(a, b) => a > b}
                  rExtent={[weightBurndownMinRecent5, weightBurndownMaxRecent5]}
                  projection="vertical"
                  oPadding={this.props.width / 28}
                  margin={{
                    left: this.props.width / 14,
                    right: this.props.width / 60,
                    top: this.props.height / 24,
                    bottom: this.props.height / 9.6
                  }}
                  annotationSettings={{
                    layout: {
                      type: 'marginalia',
                      orient: 'right',
                      textPadding: this.props.width / 21
                    }
                  }}
                />
              </div>
            </div>
          )}

        {burndownRecent &&
          burndownRecent.length !== 0 && (
            <div
              style={{
                alignItems: 'center',
                position: 'relative',
                height: this.props.height * 1.3,
                width: this.props.width
              }}
            >
              <div style={{ position: 'absolute' }}>
                <XYFrame
                  size={[this.props.width, this.props.height * 1.3]}
                  margin={{
                    left: this.props.width / 14,
                    right: this.props.width / 40,
                    top: this.props.height / 24,
                    bottom: this.props.height / 9.6
                  }}
                  xScaleType={scaleTime()}
                  xAccessor={d => new Date(d.date)}
                  yAccessor={d => d.type === 'dailyMinWeight' && d.weightRecord}
                  areas={[{ label: 'LB', coordinates: burndownRecent }]}
                  areaStyle={() => ({ fill: 'url(#paleWoodGradient)' })}
                  additionalDefs={gradient}
                  defined={d => d.weightRecord > 1}
                  axes={axesDailyOverallRecent}
                  yExtent={[weightBurndownMinRecent, weightBurndownMaxRecent]}
                />
              </div>
              <div style={{ position: 'absolute' }}>
                <OrdinalFrame
                  size={[this.props.width, this.props.height * 1.3]}
                  data={burndownRecent}
                  type={{
                    type: recentBar,
                    rotate: 180
                  }}
                  axis={axis}
                  rAccessor={d => d.weightRecord}
                  oAccessor={d => moment(new Date(d.date)).format('YYYY/MM/DD')}
                  sortO={(a, b) => a > b}
                  rExtent={[weightBurndownMinRecent, weightBurndownMaxRecent]}
                  projection="vertical"
                  oPadding={this.props.width / 28}
                  margin={{
                    left: this.props.width / 14,
                    right: this.props.width / 60,
                    top: this.props.height / 24,
                    bottom: this.props.height / 9.6
                  }}
                  annotationSettings={{
                    layout: {
                      type: 'marginalia',
                      orient: 'right',
                      textPadding: this.props.width / 21
                    }
                  }}
                />
              </div>
            </div>
          )}
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/ChartTypes/AlchemyBodyWeightWeeklyView.js